<template>
  <b-container fluid>
    <b-row v-if="!isOwner">
      <b-col>
        <AccessDenied />
      </b-col>
    </b-row>
    <div v-if="campaign && isOwner">
      <b-row>
        <b-col lg="6">
          <iq-card>
            <template v-slot:headerTitle>
              <h3 class="card-title pt-3">{{ campaign.name }}</h3>
            </template>
            <template v-slot:body>
              <b-row>
                <b-col lg="8">
                  <div class="about-info m-0 p-0">
                    <b-row>
                      <b-col cols="3">Subject:</b-col>
                      <b-col cols="9">
                        <b-link>{{ campaign.subject }}</b-link>
                      </b-col>
                      <b-col cols="3">From:</b-col>
                      <b-col cols="9">
                        <b-link>
                          {{ campaign.from_email }}
                          {{ campaign.from_name }}
                        </b-link>
                      </b-col>
                      <b-col cols="3">Audience:</b-col>
                      <b-col cols="9">
                        {{ campaign.audience || "-" }}
                      </b-col>
                      <b-col cols="3">Activity:</b-col>
                      <b-col cols="9">
                        {{ campaign.updated_at | formatDate }}
                      </b-col>
                      <b-col cols="3">Status:</b-col>
                      <b-col cols="9">
                        <CampaignStatusBadge :status="campaign.status" />
                        {{ campaign.error && `- ${campaign.error}` }}
                      </b-col>
                      <b-col cols="3">Target deal:</b-col>
                      <b-col cols="9">
                        <b-link>{{ campaign.target_deal_value || "-" }}</b-link>
                      </b-col>
                      <b-col cols="3">Template:</b-col>
                      <b-col cols="9">
                        <b-button
                          variant="link"
                          class="p-0"
                          v-b-modal.show_template
                          >Show HTML</b-button
                        >
                      </b-col>
                      <b-col cols="3" v-if="campaign.external_link"
                        >Ext. link:</b-col
                      >
                      <b-col cols="9" v-if="campaign.external_link">
                        <a
                          :href="campaign.external_link"
                          target="_blank"
                          class="external-link"
                          >{{ campaign.external_link }}</a
                        >
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>
            </template>
          </iq-card>
        </b-col>
        <b-col lg="6">
          <b-row>
            <b-col lg="6">
              <iq-card>
                <template v-slot:body>
                  <div class="d-flex align-items-center justify-content-between">
                    <h6>Delivery rate</h6>
                    <span class="iq-icon">
                      <i class="ri-information-fill"></i>
                    </span>
                  </div>
                  <div
                    class="
                      iq-customer-box
                      d-flex
                      align-items-center
                      justify-content-between
                      mt-3
                    "
                  >
                    <div class="d-flex align-items-center">
                      <div class="rounded-circle iq-card-icon iq-bg-success mr-2">
                        <i class="ri-inbox-fill"></i>
                      </div>
                      <h2 data-cy="campaign-delivery-rate">
                        {{ (campaign.delivery_rate * 100).toFixed(2) }}%
                      </h2>
                    </div>
                    <div class="iq-map text-primary font-size-32">
                      <i class="ri-bar-chart-grouped-line"></i>
                    </div>
                  </div>
                </template>
              </iq-card>
            </b-col>
            <b-col lg="6">
              <iq-card>
                <template v-slot:body>
                  <div class="d-flex align-items-center justify-content-between">
                    <h6>Open rate</h6>
                    <span class="iq-icon">
                      <i class="ri-information-fill"></i>
                    </span>
                  </div>
                  <div
                    class="
                      iq-customer-box
                      d-flex
                      align-items-center
                      justify-content-between
                      mt-3
                    "
                  >
                    <div class="d-flex align-items-center">
                      <div class="rounded-circle iq-card-icon iq-bg-warning mr-2">
                        <i class="ri-inbox-fill"></i>
                      </div>
                      <h2 data-cy="campaign-open-rate">
                        {{ (campaign.open_rate * 100).toFixed(2) }}%
                      </h2>
                    </div>
                    <div class="iq-map text-primary font-size-32">
                      <i class="ri-bar-chart-grouped-line"></i>
                    </div>
                  </div>
                </template>
              </iq-card>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="6">
              <iq-card>
                <template v-slot:body>
                  <div class="d-flex align-items-center justify-content-between">
                    <h6>CTR</h6>
                    <span class="iq-icon">
                      <i class="ri-information-fill"></i>
                    </span>
                  </div>
                  <div
                    class="
                      iq-customer-box
                      d-flex
                      align-items-center
                      justify-content-between
                      mt-3
                    "
                  >
                    <div class="d-flex align-items-center">
                      <div class="rounded-circle iq-card-icon iq-bg-info mr-2">
                        <i class="ri-inbox-fill"></i>
                      </div>
                      <h2 data-cy="campaign-click-rate">
                        {{ (campaign.ctr * 100).toFixed(2) }}%
                      </h2>
                    </div>
                    <div class="iq-map text-primary font-size-32">
                      <i class="ri-bar-chart-grouped-line"></i>
                    </div>
                  </div>
                </template>
              </iq-card>
            </b-col>
            <b-col lg="6">
              <iq-card>
                <template v-slot:body>
                  <div class="d-flex align-items-center justify-content-between">
                    <h6>Bounce rate</h6>
                    <span class="iq-icon">
                      <i class="ri-information-fill"></i>
                    </span>
                  </div>
                  <div
                    class="
                      iq-customer-box
                      d-flex
                      align-items-center
                      justify-content-between
                      mt-3
                    "
                  >
                    <div class="d-flex align-items-center">
                      <div class="rounded-circle iq-card-icon iq-bg-danger mr-2">
                        <i class="ri-inbox-fill"></i>
                      </div>
                      <h2 data-cy="campaign-bounce-rate">
                        {{ (campaign.bounce_rate * 100).toFixed(2) }}%
                      </h2>
                    </div>
                    <div class="iq-map text-primary font-size-32">
                      <i class="ri-bar-chart-grouped-line"></i>
                    </div>
                  </div>
                </template>
              </iq-card>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div>
            <b-modal id="show_template" size="xl" title="Email template">
              <div v-html="campaign.template_html"></div>
              <template v-slot:modal-footer><div></div></template>
            </b-modal>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <tab-nav :pills="true" class="nav-fill mb-3" id="pills-tab-1">
            <tab-nav-items
              :active="true"
              id="pills-home-tab-fill"
              href="#pills-home-fill"
              aria-controls="pills-home-fill"
              role="tab"
              :ariaSelected="true"
              title="Statistics"
            />
            <tab-nav-items
              v-if="isDebug"
              :active="false"
              id="pills-profile-tab-fill"
              href="#pills-profile-fill"
              aria-controls="pills-profile-fill"
              role="tab"
              :ariaSelected="false"
              data-cy="campaign-leads"
              title="Leads"
            />
            <tab-nav-items
              :active="false"
              id="pills-profile-tab-fill-2"
              href="#pills-profile-fill-2"
              aria-controls="pills-profile-fill-2"
              role="tab"
              :ariaSelected="false"
              data-cy="campaign-company-leads"
              title="Company Leads"
            />
          </tab-nav>
          <tab-content id="pills-tabContent-1" class="mt-0">
            <tab-content-item
              :active="true"
              id="pills-home-fill"
              aria-labelled-by="pills-home-tab-fill"
            >
              <b-row>
                <b-col lg="12">
                  <iq-card>
                    <template v-slot:headerTitle>
                      <h4 class="card-title pt-3">Delivery progress & Opens</h4>
                    </template>
                    <template v-slot:body>
                      <b-row>
                        <b-col lg="12">
                          <ApexChart
                            :element="calculatedChartCampaign.type"
                            :chartOption="calculatedChartCampaign.bodyData"
                          />
                        </b-col>
                      </b-row>
                    </template>
                  </iq-card>
                </b-col>
                <!-- <b-col lg="4">
                  <iq-card>
                    <template v-slot:headerTitle>
                      <h4 class="card-title pt-3">Platforms</h4>
                    </template>
                    <template v-slot:body>
                      <b-row>
                        <b-col lg="12">
                          <ApexChart
                            element="home-chart-06"
                            :chartOption="chartPlatforms"
                          />
                        </b-col>
                      </b-row>
                    </template>
                  </iq-card>
                </b-col>
                <b-col lg="4">
                  <iq-card>
                    <template v-slot:headerTitle>
                      <h4 class="card-title pt-3">Browsers</h4>
                    </template>
                    <template v-slot:body>
                      <b-row>
                        <b-col lg="12">
                          <ApexChart
                            element="home-chart-04"
                            :chartOption="chartBrowsers"
                          />
                        </b-col>
                      </b-row>
                    </template>
                  </iq-card>
                </b-col>
                <b-col lg="4">
                  <iq-card>
                    <template v-slot:headerTitle>
                      <h4 class="card-title pt-3">Devices</h4>
                    </template>
                    <template v-slot:body>
                      <b-row>
                        <b-col lg="12">
                          <ApexChart
                            element="home-chart-05"
                            :chartOption="chartDevices"
                          />
                        </b-col>
                      </b-row>
                    </template>
                  </iq-card>
                </b-col>-->
              </b-row>
            </tab-content-item>
            <tab-content-item
              :active="false"
              id="pills-profile-fill"
              aria-labelled-by="pills-profile-tab-fill"
            >
              <b-row>
                <b-col lg="12">
                  <LeadsTable
                    :defaultSelectedCampaign="campaign.id"
                    v-if="campaign.id"
                  />
                </b-col>
              </b-row>
            </tab-content-item>
            <tab-content-item
              :active="false"
              id="pills-profile-fill-2"
              aria-labelled-by="pills-profile-tab-fill-2"
            >
              <b-row>
                <b-col lg="12">
                  <CompanyLeadsTable
                    :defaultSelectedCampaign="campaign.id"
                    v-if="campaign.id"
                  />
                </b-col>
              </b-row>
            </tab-content-item>
          </tab-content>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>
<style scoped>
.carditem {
  height: 200px;
  width: 150px;
  display: flex;
  flex-direction: column-reverse;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 3rem;
  position: relative;
  background: url("../../assets/images/newsletter-placeholder.png");
  /* background: url(https://assets.hongkiat.com/uploads/free-business-newsletter-templates/preview/2-hola.jpg); */
  background-size: cover;
}
.carditem__dimmer {
  opacity: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  position: absolute;
  transition: 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1rem;
}
.carditem__dimmer:hover {
  opacity: 1;
}
.carditem__title {
  opacity: 0.85;
  text-align: center;
  width: 100%;
  padding: 1rem;
  background: linear-gradient(
    to right,
    var(--iq-primary) 0%,
    var(--iq-primary-light) 100%
  );
}
.carditem__title .h6 {
  color: white;
  margin-bottom: 0;
  opacity: 1;
}
.external-link {
  color: #007bff !important;
}
.external-link:hover {
  color: #0056b3 !important;
  text-decoration: underline !important;
}
</style>
<script>
import { core } from "../../config/pluginInit";
import { db } from "../../config/firebase";
import ApexChart from "../../components/core/charts/ApexChart";
import gql from "graphql-tag";
import CampaignStatusBadge from "./Components/CampaignStatusBadge";
import LeadStageBadge from "./Components/LeadStageBadge";
import LeadsTable from "./Components/LeadsTable.vue";
import CompanyLeadsTable from "./Components/CompanyLeadsTable.vue";
import {isDebug} from '../../Utils/account';
import { getAccountSync } from "../../Utils/account";
import AccessDenied from './Components/AccessDenied.vue';

const GET_CAMPAIGN_DETAIL = gql`
  query getCampaignDetail($id: uuid!) {
    campaigns_by_pk(id: $id) {
      id
      name
      subject
      from_email
      from_name
      updated_at
      target_deal_value
      audience
      status
      delivery_rate
      open_rate
      ctr
      error
      bounce_rate
      template_html
      external_link
      leads {
        id
        stage_id
        campaign {
          id
          name
        }
        updated_at
        deal_value
        cluster
        contact {
          id
          first_name
          last_name
          company {
            id
            name
          }
        }
      }
      charts
      owner_id
    }
  }
`;

export default {
  name: "CampaignsDetail",
  mounted() {
    core.index();
  },
  components: { ApexChart, CampaignStatusBadge, LeadStageBadge, LeadsTable, CompanyLeadsTable, AccessDenied },
  data() {
    return {
      account: getAccountSync(),
      campaign: null,
      chartPlatforms: {
        series: [44, 55, 13, 33],
        chart: {
          width: 380,
          height: 180,
          type: "donut"
        },
        dataLabels: {
          enabled: false
        },
        colors: ["#827af3", "#6ce6f4", "#a09e9e", "#fbc647"],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                show: false
              }
            }
          }
        ],
        legend: {
          position: "right",
          offsetY: 0,
          height: 150
        }
      },
      chartBrowsers: {
        series: [13, 44, 33, 51, 7],
        chart: {
          width: 380,
          height: 180,
          type: "donut"
        },
        dataLabels: {
          enabled: false
        },
        colors: ["#827af3", "#6ce6f4", "#a09e9e", "#fbc647"],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                show: false
              }
            }
          }
        ],
        legend: {
          position: "right",
          offsetY: 0,
          height: 150
        }
      },
      chartDevices: {
        series: [34, 44, 19, 12, 52],
        chart: {
          width: 380,
          height: 180,
          type: "donut"
        },
        dataLabels: {
          enabled: false
        },
        colors: ["#827af3", "#6ce6f4", "#a09e9e", "#fbc647"],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                show: false
              }
            }
          }
        ],
        legend: {
          position: "right",
          offsetY: 0,
          height: 150
        }
      },
      chartCampaign: {
        title: "Line Area Chart",
        type: "line-area",
        bodyData: {
          chart: {
            height: 350,
            type: "area"
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: "smooth"
          },
          colors: ["#0084ff", "#00ca00"],
          series: [
            {
              name: "series1",
              data: [31, 40, 28, 51, 42, 109, 100]
            },
            {
              name: "series2",
              data: [11, 32, 45, 32, 34, 52, 41]
            }
          ],

          xaxis: {
            type: "datetime",
            categories: [
              "2018-09-19T00:00:00",
              "2018-09-19T01:30:00",
              "2018-09-19T02:30:00",
              "2018-09-19T03:30:00",
              "2018-09-19T04:30:00",
              "2018-09-19T05:30:00",
              "2018-09-19T06:30:00"
            ]
          },
          tooltip: {
            x: {
              format: "dd/MM/yy HH:mm"
            }
          }
        }
      }
    };
  },
  apollo: {
    campaign() {
      return {
        query: GET_CAMPAIGN_DETAIL,
        fetchPolicy: "no-cache",
        variables: {
          id: this.$route.params.id
        },
        update: data => data.campaigns_by_pk
      };
    }
  },
  computed: {
    fullName: function() {
      return this.user.fname + " " + this.user.lname;
    },
    calculatedChartCampaign: function() {
      return {
        title: "Line Area Chart",
        type: "line-area",
        bodyData: {
          chart: {
            height: 350,
            type: "area"
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: "smooth"
          },
          colors: ["#0084ff", "#00ca00"],
          series: this.campaign.charts.opensClicks.datasets.map(ds => ({
            name: ds.Label.replace("_", " ").toLowerCase(),
            data: ds.Values
          })),
          xaxis: {
            type: "datetime",
            categories: this.campaign.charts.opensClicks.xAxis
          },
          tooltip: {
            x: {
              format: "dd/MM/yy HH:mm"
            }
          }
        }
      };
    },
    isDebug: function() {
      return isDebug();
    },
    isOwner(){
      if (this?.campaign?.owner_id === this?.account?.userId) {
        return true
      }
      return false
    }
  },
  methods: {
    onSubmit() {
      this.user.name = this.fullName;
      db.collection("users").add(this.user);
      core.showSnackbar("success", "User has been updated successfully.");
      this.$router.replace("/user/user-list");
    },
    previewImage: function(event) {
      const input = event.target;

      if (input.files && input.files[0]) {
        const reader = new FileReader();

        reader.onload = e => {
          this.user.profile_image = e.target.result;
        };

        reader.readAsDataURL(input.files[0]);
      }
    }
  }
};
</script>
